// Host shell layout: top navigation + active workspace area + the omnipresent,
// collapsible ESA panel (§4.2 wireframe, §11.1). The ESA panel persists across
// workspace navigation.

import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Resizable } from 're-resizable';
import { ToastContainer } from 'react-toastify';
import { Icon } from '@growthaxl/web-foundation';
import { GlobalHeader } from '../../features/Navigation/components/GlobalHeader';
import { WORKSPACE_CATALOG } from '../../features/Navigation/workspaceCatalog';
import { WorkspaceOutlet } from '../../features/Workspaces/WorkspaceOutlet';
import { EsaPanel } from '../../features/Esa/components/EsaPanel';
import { EsaShellEffectsBridge } from '../../features/Esa/components/EsaShellEffectsBridge';
import { AwarenessShellBridge } from '../../features/Canvas/bridges/AwarenessShellBridge';
import { CanvasOverlayManager } from '../../features/Canvas/overlays/CanvasOverlayManager';
import { useShellStore } from '../../shell/store/shellStore';
import { useEsaDomainStore } from '../../features/Esa/store/esaDomainStore';
import { clearNewConversationSession } from '../../features/Esa/hooks/useReturningUser';
import { useNotificationStream } from '../../features/Esa/hooks/useNotificationStream';
import { useSolutionsCatalogSync } from '../../features/Esa/hooks/useSolutionsCatalogSync';
import { useToastSubscriber } from '../../shell/events/useToastSubscriber';
import { useAuth } from '../../features/Auth/hooks/useAuth';
import { getActiveProductLine } from '../../adapters/AdapterRegistry';
import { Loader } from './Loader';
import { ShellBootstrapProvider } from './ShellBootstrapContext';
import { config } from '../../config';
import { USER_THEME_STORAGE_KEY } from '../../constants/storageKeys';
import {
  ESA_CANVAS_DIVIDER_WIDTH,
  PANEL_MAX_RATIO,
  PANEL_MIN_RATIO,
  HIL_TOAST_AUTO_CLOSE_MS,
  HIL_TOAST_CONTAINER_ID,
  TOAST_CONTAINER_AUTO_CLOSE_MS,
} from '../../constants/timing';

/** ESA panel visibility — driven by product-line `features.esaPanel` (default true). */
function useEsaPanelEnabled(): boolean {
  return getActiveProductLine()?.features?.esaPanel !== false;
}

/* ── Collapsed rail icons (foundation) ── */
const ICN_PLUS = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Vertical grip bar between ESA panel and canvas (resize handle when panel is open). */
function EsaCanvasDividerBar(): React.ReactElement {
  return (
    <div
      className="esa-canvas-divider"
      data-testid="esa-canvas-divider"
      aria-hidden="true"
    >
      <span className="esa-canvas-divider__grip" />
    </div>
  );
}

const ESA_CANVAS_DIVIDER_HANDLE_STYLES = {
  right: {
    width: ESA_CANVAS_DIVIDER_WIDTH,
    right: -ESA_CANVAS_DIVIDER_WIDTH,
    height: '100%',
    top: 0,
    cursor: 'col-resize',
    padding: 0,
    margin: 0,
    zIndex: 2,
  },
} as const;

/** Ghost icon button for the collapsed rail. */
function RailIconButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex items-center justify-center w-8 h-8 rounded-6 border-none bg-transparent cursor-pointer text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
    >
      {icon}
    </button>
  );
}

/**
 * Collapsed ESA rail — shown when the ESA panel is hidden.
 * Three actions stacked vertically (no identity mark):
 *   1. Expand panel      → opens the full panel in current mode
 *   2. My Inbox          → opens the panel in thread-list mode
 *   3. New conversation  → opens the panel in briefing mode
 */
function CollapsedESARail({
  onExpand,
  onInbox,
  onNewConversation,
  hitlCount,
}: {
  onExpand: () => void;
  onInbox: () => void;
  onNewConversation: () => void;
  hitlCount: number;
}): React.ReactElement {
  return (
    <div className="flex h-full min-h-0 shrink-0">
      <div className="flex h-full w-10 flex-col items-center pt-3 bg-esa-panel">
      {/* 1 — Expand */}
      <RailIconButton
        icon={<Icon name="sidebar_left_open" className="!size-6 shrink-0" />}
        label="Expand ESA panel"
        onClick={onExpand}
      />

      {/* Divider */}
      <div className="w-6 border-t border-border-default my-1" />

      {/* 2 — My Inbox (with notification dot when hitlCount > 0) */}
      <div className="relative">
        <RailIconButton
          icon={
            <Icon
              name={hitlCount > 0 ? 'direct_notification_active' : 'direct_notification'}
              className="!size-6 shrink-0"
            />
          }
          label="My Inbox"
          onClick={onInbox}
        />
        {hitlCount > 0 && (
          <span className="absolute top-1 right-1 size-2 rounded-full bg-error pointer-events-none" />
        )}
      </div>

      {/* Divider */}
      <div className="w-6 border-t border-border-default my-1" />

      {/* 3 — New Conversation */}
      <RailIconButton
        icon={ICN_PLUS}
        label="New conversation"
        onClick={onNewConversation}
      />
      </div>
      <EsaCanvasDividerBar />
    </div>
  );
}

/** Panel ratio bounds — panel width is always (ratio × viewport width). */
function clampRatio(r: number): number {
  return Math.max(PANEL_MIN_RATIO, Math.min(PANEL_MAX_RATIO, r));
}

/** Derive the current pixel width from the stored ratio. */
function ratioToPx(ratio: number): number {
  return Math.round(window.innerWidth * ratio);
}

export const Layout: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const notificationStreamEnabled =
    getActiveProductLine()?.features?.notificationStream !== false;
  useNotificationStream(isLoggedIn && notificationStreamEnabled);
  const showEsaPanel = useEsaPanelEnabled();
  const { isReady: catalogReady } = useSolutionsCatalogSync();
  useToastSubscriber();

  const esaPanelOpen = useShellStore(state => state.esaPanelOpen);
  const setEsaPanelOpen = useShellStore(state => state.setEsaPanelOpen);
  const setEsaPanelMode = useShellStore(state => state.setEsaPanelMode);
  const panelRatio = useShellStore(state => state.esaPanelRatio);
  const setEsaPanelRatio = useShellStore(state => state.setEsaPanelRatio);
  const breadcrumbs = useShellStore(state => state.breadcrumbs);
  const storeTheme = useShellStore(state => state.personalization?.theme);
  const themeMode = storeTheme ?? (typeof window !== 'undefined' && localStorage.getItem(USER_THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light');

  // Derived pixel width — recomputed on every render so it always reflects
  // the current viewport. The Resizable component receives this as its size.
  const panelWidth = ratioToPx(panelRatio);

  // Re-derive pixel width on window resize (triggers a re-render which
  // recomputes panelWidth from the unchanged ratio).
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    window.addEventListener('resize', forceUpdate);
    return () => window.removeEventListener('resize', forceUpdate);
  }, []);

  const pendingHITL = useEsaDomainStore(s => s.pendingHITL);
  const setActiveThread = useEsaDomainStore(s => s.setActiveThread);
  const hitlCount = pendingHITL.length;

  const handleExpand = (): void => {
    setEsaPanelOpen(true);
  };

  const handleInbox = (): void => {
    setEsaPanelMode('inbox');
    setEsaPanelOpen(true);
  };

  const handleNewConversation = (): void => {
    // Clear persisted session thread so useReturningUser doesn't restore the
    // old thread when EsaPanel mounts fresh for this new conversation.
    clearNewConversationSession();
    setActiveThread(null);
    setEsaPanelMode('briefing');
    setEsaPanelOpen(true);
  };

  return (
    <ShellBootstrapProvider catalogReady={catalogReady}>
    <div className="relative flex h-screen flex-col overflow-hidden bg-canvas text-text-primary">
      {!catalogReady && (
        <div
          data-testid="shell-initial-loader"
          className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center"
          aria-busy="true"
        >
          <Loader data-testid="shell-catalog-loader" />
        </div>
      )}
      <EsaShellEffectsBridge />
      <AwarenessShellBridge />
      <GlobalHeader className="shrink-0" />
      {/* <TopNav /> */}

      {breadcrumbs.length > 0 && (
        <nav
          aria-label="breadcrumb"
          className="flex shrink-0 items-center gap-1 border-b border-border-default bg-surface px-4 py-1.5 text-xs text-text-secondary"
        >
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="mx-1 text-text-tertiary" aria-hidden="true">/</span>}
              {crumb.path ? (
                <a href={crumb.path} className="hover:text-text-primary hover:underline">
                  {crumb.label}
                </a>
              ) : (
                <span className={i === breadcrumbs.length - 1 ? 'font-medium text-text-primary' : ''}>
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {showEsaPanel &&
          (esaPanelOpen ? (
            <div
              className="esa-panel-shell flex h-full min-h-0 shrink-0 overflow-visible"
              style={{ width: panelWidth + ESA_CANVAS_DIVIDER_WIDTH }}
            >
              <Resizable
                size={{ width: panelWidth, height: '100%' }}
                minWidth={ratioToPx(PANEL_MIN_RATIO)}
                maxWidth={ratioToPx(PANEL_MAX_RATIO)}
                enable={{ right: true }}
                onResizeStop={(_e, _dir, _ref, delta) => {
                  // Convert the new absolute pixel width back to a ratio so future
                  // window resizes continue to scale the panel proportionally.
                  const newPx = panelWidth + delta.width;
                  setEsaPanelRatio(clampRatio(newPx / window.innerWidth));
                }}
                handleComponent={{ right: <EsaCanvasDividerBar /> }}
                handleStyles={ESA_CANVAS_DIVIDER_HANDLE_STYLES}
                handleWrapperClass="esa-canvas-divider-handle"
                className="relative h-full min-h-0 shrink-0 overflow-visible"
                style={{ height: '100%', overflow: 'visible' }}
              >
                <EsaPanel />
              </Resizable>
            </div>
          ) : (
            <CollapsedESARail
              onExpand={handleExpand}
              onInbox={handleInbox}
              onNewConversation={handleNewConversation}
              hitlCount={hitlCount}
            />
          ))}

        <main className="min-w-0 flex-1 overflow-auto bg-canvas">
          <CanvasOverlayManager>
            <Routes>
              {WORKSPACE_CATALOG.map(w => (
                <Route
                  key={w.id}
                  path={`${w.route}/*`}
                  element={<WorkspaceOutlet workspace={w} />}
                />
              ))}
              <Route path="/" element={<Navigate to={config.defaultLandingPath} replace />} />
              <Route path="*" element={<Navigate to={config.defaultLandingPath} replace />} />
            </Routes>
          </CanvasOverlayManager>
        </main>
      </div>

      {/* <footer className="flex h-6 items-center justify-between border-t border-border-default bg-surface-secondary px-3 text-xs text-text-secondary">
        <span>v0.1.0</span>
        <span>
          {getActiveProductLineId()} · auth:{getAuthAdapter().name} · bff:{getBffAdapter().name}
        </span>
      </footer> */}

      <ToastContainer position="top-right" autoClose={TOAST_CONTAINER_AUTO_CLOSE_MS} theme={themeMode === 'dark' ? 'dark' : 'light'} />
      <ToastContainer
        containerId={HIL_TOAST_CONTAINER_ID}
        position="bottom-right"
        autoClose={HIL_TOAST_AUTO_CLOSE_MS}
        closeButton={false}
        hideProgressBar
        pauseOnFocusLoss={false}
        theme={themeMode === 'dark' ? 'dark' : 'light'}
      />
    </div>
    </ShellBootstrapProvider>
  );
};
